import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_Property() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">⚙️ Advanced CSS: Property Management</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS properties define the visual presentation of HTML elements. Advanced
        usage involves combining shorthand, logical properties, transitions, and
        custom properties to create responsive and dynamic UI.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg,#fff1f2,#ffe4e6)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#b91c1c" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Shorthand properties reduce CSS verbosity.</li>
          <li>Logical properties improve responsive layouts.</li>
          <li>Combine gradients, shadows, and images for rich visuals.</li>
          <li>
            Use <code>transition</code> and <code>animation</code> for smooth
            effects.
          </li>
          <li>
            Custom properties (<code>--var</code>) allow dynamic changes and
            theme management.
          </li>
        </ul>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">📝 Examples of CSS Properties</h2>

        {/* 1. Shorthand Properties */}
        <h3>1. Shorthand Properties</h3>
        <CodeCopyBlock
          code={`.box {
  margin: 10px 20px 15px 5px; /* top right bottom left */
  padding: 12px 24px; /* top/bottom right/left */
  border: 2px solid #3b82f6;
  border-radius: 12px;
}`}
        />
        <div
          style={{
            margin: "10px 20px 15px 5px",
            padding: "12px 24px",
            border: "2px solid #3b82f6",
            borderRadius: "12px",
            background: "#dbeafe",
            marginBottom: "1rem",
          }}
        >
          Shorthand Box
        </div>

        {/* 2. Logical Properties */}
        <h3>2. Logical Properties for Layout</h3>
        <CodeCopyBlock
          code={`.container {
  padding-block: 20px; /* top & bottom */
  padding-inline: 16px; /* left & right */
  margin-block-start: 10px;
}`}
        />
        <div
          style={{
            paddingBlock: "20px",
            paddingInline: "16px",
            marginBlockStart: "10px",
            background: "#fef3c7",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        >
          Logical Padding Container
        </div>

        {/* 3. Background Properties */}
        <h3>3. Backgrounds & Gradients</h3>
        <CodeCopyBlock
          code={`.gradient-box {
  background: linear-gradient(135deg,#3b82f6,#9333ea);
  background-size: cover;
  background-repeat: no-repeat;
}`}
        />
        <div
          style={{
            background: "linear-gradient(135deg,#3b82f6,#9333ea)",
            padding: "20px",
            borderRadius: "12px",
            color: "white",
            marginBottom: "1rem",
          }}
        >
          Gradient Background Box
        </div>

        {/* 4. Transitions & Animations */}
        <h3>4. Transitions & Animations</h3>
        <CodeCopyBlock
          code={`.animated-box {
  width: 100px;
  height: 100px;
  background: #f97316;
  transition: transform 0.3s ease, background 0.3s ease;
}
.animated-box:hover {
  transform: scale(1.2);
  background: #f43f5e;
}`}
        />
        <div
          style={{
            width: "100px",
            height: "100px",
            background: "#f97316",
            transition: "transform 0.3s ease, background 0.3s ease",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.2)";
            e.currentTarget.style.background = "#f43f5e";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.background = "#f97316";
          }}
        ></div>

        {/* 5. Custom Properties & Fallback */}
        <h3>5. Custom Properties with Fallback</h3>
        <CodeCopyBlock
          code={`:root {
  --primary-color: #3b82f6;
}
.button {
  background: var(--primary-color, #9333ea); /* fallback if variable undefined */
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
}`}
        />
        <button
          style={{
            background: "var(--primary-color,#3b82f6)",
            color: "white",
            padding: "10px 20px",
            borderRadius: "8px",
            marginBottom: "2rem",
          }}
        >
          Custom Property Button
        </button>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg,#fff1f2,#ffe4e6)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#b91c1c" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine shorthand and logical properties for responsive, clean
            layouts.
          </li>
          <li>Use gradients, patterns, and images for advanced backgrounds.</li>
          <li>
            Leverage transitions & animations for interactive UI elements.
          </li>
          <li>
            Use custom properties for theming, fallbacks, and dynamic updates.
          </li>
        </ul>
      </div>
    </div>
  );
}
